import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import axiosInstance from '../lib/axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { FileText, Plus, LogOut, Trash2, Settings, Shield, ImageOff } from 'lucide-react';
import { Label } from '../components/ui/label';
// El ConfigPanel canónico es el de tiptap (superconjunto: añade los selectores
// de modelo de imagen y de visión). El de components/ solo exponía apiKey+model,
// así que quien configuraba desde el Dashboard nunca veía esos dos ajustes.
import ConfigPanel from '../tiptap/components/ConfigPanel';
import ConfirmDialog from '../components/ConfirmDialog';
import ThemeToggle from '../components/ThemeToggle';
import HelpPanel from '../components/HelpPanel';
import Footer from '../components/Footer';
import ImageGallery from '../tiptap/components/ImageNode/dialogs/ImageGallery';
import ImageLightboxHost from '../tiptap/components/ImageLightbox/ImageLightboxHost';

const DashboardPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, docId: null, docTitle: '' });
  const [galleryCleanupOpen, setGalleryCleanupOpen] = useState(false);
  const { token, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axiosInstance.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      // Don't show toast here, axios interceptor will handle it
    } finally {
      setLoading(false);
    }
  };

  const createDocument = async () => {
    if (!newDocTitle.trim()) {
      toast.error('El título no puede estar vacío');
      return;
    }

    try {
      const response = await axiosInstance.post('/documents', {
        title: newDocTitle,
        editor_type: 'tiptap'
      });
      toast.success('Documento creado');
      setDocuments([...documents, response.data]);
      setNewDocTitle('');
      setDialogOpen(false);

      // Navigate to the unified document route
      navigate(`/document/${response.data.id}`);
    } catch (error) {
      console.error('Error creating document:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al crear documento');
      }
    }
  };

  const deleteDocument = (docId, docTitle, e) => {
    e.stopPropagation();
    setDeleteConfirm({
      open: true,
      docId: docId,
      docTitle: docTitle
    });
  };

  // TipTap es el ÚNICO editor (P15): abrir un documento va directo, sin el
  // diálogo "Seleccionar Editor" de una sola opción (petición del dueño).
  const openDocument = (doc, e) => {
    e.stopPropagation();
    navigate(`/document/${doc.id}`);
  };

  const confirmDeleteDocument = async () => {
    const { docId } = deleteConfirm;

    try {
      await axiosInstance.delete(`/documents/${docId}`);
      toast.success('Documento eliminado');
      setDocuments(documents.filter(d => d.id !== docId));
    } catch (error) {
      console.error('Error deleting document:', error);
      if (error.response?.status !== 401) {
        toast.error('Error al eliminar documento');
      }
    } finally {
      setDeleteConfirm({ open: false, docId: null, docTitle: '' });
    }
  };

  return (
    <div className="min-h-screen app-gradient flex">
      <HelpPanel page="dashboard" />
      {/* T-126: columna en flex-col para que el Footer baje al fondo (sticky
          footer). Sin esto se sentaba bajo las tarjetas, a media pantalla, con
          una banda de fondo vacia abajo. */}
      <div className="flex-1 min-w-0 flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-4 py-8 flex-1">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-sky-400 to-blue-500 p-3 rounded-xl shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                SapiensDoc
              </h1>
              <p className="text-slate-600 dark:text-slate-300">Hola, {user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <ThemeToggle className="glass border-0" />
            {user?.role === 'admin' && (
              <Button
                data-testid="admin-button"
                variant="outline"
                onClick={() => navigate('/admin', { state: { from: location.pathname + location.search } })}
                title="Panel de administración"
                className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
              >
                <Shield className="w-5 h-5 text-purple-600" />
              </Button>
            )}
            <Button
              data-testid="cleanup-images-button"
              variant="outline"
              onClick={() => setGalleryCleanupOpen(true)}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
              title="Limpiar imágenes no utilizadas"
            >
              <ImageOff className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </Button>
            <Button
              data-testid="config-button"
              variant="outline"
              onClick={() => setConfigOpen(true)}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
            >
              <Settings className="w-5 h-5" />
            </Button>
            <Button
              data-testid="logout-button"
              variant="outline"
              onClick={logout}
              className="glass border-0 hover:bg-white/80 dark:hover:bg-slate-700/80 transition-all"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Documents Grid */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Mis Documentos
            </h2>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-doc-button"
                  className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Nuevo Documento
                </Button>
              </DialogTrigger>
              <DialogContent className="glass border-0">
                <DialogHeader>
                  <DialogTitle>Crear Documento</DialogTitle>
                  <DialogDescription>
                    Ingresa un título para tu nuevo documento.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="doc-title">Título del documento</Label>
                    <Input
                      id="doc-title"
                      data-testid="new-doc-title-input"
                      placeholder="Título del documento"
                      value={newDocTitle}
                      onChange={(e) => setNewDocTitle(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && createDocument()}
                      className="mt-2"
                    />
                  </div>

                  <Button
                    data-testid="create-doc-submit"
                    onClick={createDocument}
                    className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                  >
                    Crear Documento
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600 dark:text-slate-300">Cargando...</p>
            </div>
          ) : documents.length === 0 ? (
            <Card className="glass border-0 text-center py-12">
              <CardContent>
                <FileText className="w-16 h-16 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 dark:text-slate-300 mb-4">No tienes documentos aún</p>
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                >
                  Crear tu primer documento
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => {
                return (
                  <Card
                    key={doc.id}
                    data-testid={`document-card-${doc.id}`}
                    className="glass border-0 cursor-pointer hover:shadow-xl transition-all group"
                    onClick={(e) => openDocument(doc, e)}
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-8 h-8 text-sky-500" />
                        </div>
                        <Button
                          data-testid={`delete-doc-${doc.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={(e) => deleteDocument(doc.id, doc.title, e)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <h3 className="font-semibold text-lg mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {doc.title}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {new Date(doc.updated_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
        <Footer />
      </div>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="glass border-0 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configuración de IA</DialogTitle>
            <DialogDescription>
              Configura tu API key, modelo y prompt del sistema para la IA.
            </DialogDescription>
          </DialogHeader>
          <ConfigPanel />
        </DialogContent>
      </Dialog>

      {/* El panel de administración vive ahora en la ruta /admin (no modal). */}

      {/* Delete Document Confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => !open && setDeleteConfirm({ open: false, docId: null, docTitle: '' })}
        title="Eliminar documento"
        description={`¿Estás seguro de que deseas eliminar el documento "${deleteConfirm.docTitle}"? Esta acción no se puede deshacer.`}
        onConfirm={confirmDeleteDocument}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
      />

      {/* Gallery cleanup modal (reuses ImageGallery in cleanupOnly mode) */}
      <ImageGallery
        open={galleryCleanupOpen}
        onOpenChange={setGalleryCleanupOpen}
        cleanupOnly
        onSelectImage={() => {}}
      />
      <ImageLightboxHost />

    </div>
  );
};

export default DashboardPage;
